
// React and Router
import React, { useEffect, lazy } from 'react';
import { Navigate, useLocation, useRoutes, Outlet } from 'react-router-dom';

// Redux
import { useSelector } from 'react-redux';

// MUI
import { Container, Box } from '@mui/material';

// Page and Component Imports (grouped)
const CreateJobDebriefing = lazy(() => import('../pages/CreateJobDebriefing.jsx'));
const RolePlaybookPage = lazy(() => import('../pages/RolePlaybook/index.jsx'));
const PublicRolePlaybookPage = lazy(() => import('../pages/RolePlaybook/PublicRolePlaybookPage.jsx'));
const Setforgotpassword = lazy(() => import('../pages/setforgotpassword'));
const Signin = lazy(() => import('../pages/signin'));
const Signup = lazy(() => import('../pages/signup'));
const TalentPoolNew = lazy(() => import('../pages/talent_pool_new'));
const ClientTalentPoolNew = lazy(() => import('../pages/talent_pool_new_client'));
const JobDetails = lazy(() => import('../pages/JobDetails'));
const JobPage = lazy(() => import('../pages/job-new'));
const HomeLayout = lazy(() => import('../Layout/HomeLayout.jsx'));
const TeamMembers = lazy(() => import('../pages/add_and_edit_users.js'));
const ImpersonateUsers = lazy(() => import('../pages/ImpersonateUsers/ImpersonateUsers.js'));
const AddPod = lazy(() => import('../pages/pod_add_page'));
const Change_password = lazy(() => import('../pages/Change_password'));
const Applications = lazy(() => import('../pages/applications_new'));
// import Applications from "../pages/applications_new";
const Dashboard = lazy(() => import('../pages/Dashborad'));
const MainDashboardComponent = lazy(() => import('../pages/main-dashboard.js'));
const DailyReportComponent = lazy(() => import('../components/operationalDashboard/index.js'));
const JobAnalysis = lazy(() => import('../pages/job-analysis.js'));
const CandidateReport = lazy(() => import('../pages/Tracking/index.js'));
const JobAnalyticsNew = lazy(() => import('../pages/JobAnalytics/JobAnalyticsNew.jsx'));
const Scorecard = lazy(() => import('../pages/scorcard'));
const AddMember = lazy(() => import('../pages/add_member'));
const NewTalentCard = lazy(() => import('../components/Talent-Card'));
const NewTalentCardUI = lazy(() => import('../components/FinalTalentCard'));
const ClientTab = lazy(() => import('../components/clientTab/ClientsJob'));
const AddingToken = lazy(() => import('../components/candidate/Forms/AddingToken'));
const OAuthFailed = lazy(() => import('../components/candidate/Forms/OAuthFailed'));
const Choose = lazy(() => import('../pages/Choose.js'));
const EditProfile = lazy(() => import('../pages/EditProfile.js'));
const Settings = lazy(() => import('../pages/Settings.js'));
const Branding = lazy(() => import('../pages/Branding.js'));
const AdminDashboard = lazy(() => import('../pages/AdminDashboard/AdminDashboard.js'));
const HiringReport = lazy(() => import('../pages/HiringReport/HiringReport.js'));
const InterviewListTab = lazy(() => import('../pages/InterviewListTab'));
const MappingTab = lazy(() => import('../pages/Market-Intelligence/MappingTab'));
const MappedCandidates = lazy(() => import('../pages/Market-Intelligence/MappedCandidates'));
const ShareMappedTalent = lazy(() => import('../pages/Market-Intelligence/ShareMappedTalent'));
const PreviewCompany = lazy(() => import('../pages/PreviewCompany.js'));
const MarketIntelligenceDashboard = lazy(() => import('../pages/Market-Intelligence/PreeSales/MarketIntelligenceDashboard.jsx'));
const Change_Password_Admin = lazy(() => import('../pages/Change_Password_Admin.js'));
const AccountView = lazy(() => import('../pages/AccountView/index.js'));
const SlackCallback = lazy(() => import('../pages/SlackCallback.jsx'));
const TalentPartnerPhotoUpload = lazy(() => import('../pages/TalentPartnerPhotoUpload'));
 import NotFound from '../pages/NotFound.jsx';
// Direct imports (not lazy)
import ExpiryPage from '../pages/LinkExpiry.js';
// import FinalTalentCard from '../components/FinalTalentCard/index.js';
const FinalTalentCard = lazy(() => import('../components/FinalTalentCard/index.js'));
import NavBar from '../components/navbar/DrawerAppBar';

// Excluded URLs for auth redirect
const excludeUrl = [
  '/signin',
  '/signup',
  '/forgot-password',
  '/setforgotpassword/',
  '/member',
  '/auth/social',
];

// Auth wrapper for protected routes
function ProtectedRoute({ children }) {
  const { auth } = useSelector(state => state.user.user);
  const loading = useSelector(state => state.user.loading);
  const location = useLocation();
  if (loading) return <div className="loader"></div>;
  if (!auth) {
    const authToken = localStorage.getItem('accessToken');
    if (!authToken && !excludeUrl.some(u => location.pathname.startsWith(u))) {
      localStorage.setItem('prevURL', location.pathname + location.search);
    }
    return <Navigate to="/signin" />;
  }
  return children;
}

// Applications route: allow access if candidateId is present (public share),
// otherwise require authentication
function ApplicationRoute({ children }) {
  const { auth } = useSelector(state => state.user.user);
  const loading = useSelector(state => state.user.loading);
  const location = useLocation();
  const candidateId = new URLSearchParams(window.location.search).get("candidateId");
  if (loading) return <div className="loader"></div>;
  if (candidateId) return children;
  if (!auth) {
    const authToken = localStorage.getItem('accessToken');
    if (!authToken) {
      localStorage.setItem('prevURL', location.pathname + location.search);
    }
    return <Navigate to="/signin" />;
  }
  return children;
}

export default function Routes() {
  const location = useLocation();
  const userRole = useSelector(state => state.user.user);
  const authToken = localStorage.getItem('accessToken');
  useEffect(() => {
    if (!authToken && !excludeUrl.some(url => location.pathname.startsWith(url))) {
      localStorage.setItem('prevURL', location.pathname + location.search);
    }
  }, [authToken, location]);

  const routes = useRoutes([
    // Public routes
    { path: '/signin', element: <Signin /> },
    { path: '/signup', element: <Signup /> },
    { path: '/choose', element: <Choose /> },
    { path: '/setforgotpassword/:token', element: <Setforgotpassword /> },
    { path: '/auth/social/:token/:purpose', element: <AddingToken /> },
    { path: '/auth/social/failed', element: <OAuthFailed /> },
    { path: '/member/:token', element: <AddMember /> },

    // Slack OAuth callback
    { path: '/slack/callback', element: <SlackCallback /> },

    // Public sales routes (like public)
    { path: '/talent-pool', element: <HomeLayout maxWidth="lg"><TalentPoolNew /></HomeLayout> },
    { path: '/job/:job_id/candidate/:cand_id/:stage/sales', element: <Container maxWidth="lg"><FinalTalentCard /></Container> },
    { path: '/talent_pool/candidate/:cand_id/:stage/public', element: <Container maxWidth="lg"><NewTalentCardUI /></Container> },
    { path: '/job/:job_id/candidate/:cand_id/:stage/public', element: <Container maxWidth="lg"><NewTalentCardUI /></Container> },
    { path: '/talent-pool/:sharedpoolId/:publicOrCustom', element: <HomeLayout><TalentPoolNew isPublic={true} /></HomeLayout> },
    // MarketIntelligenceDashboard public route
    { path: '/market-intel/:id/sample', element: <HomeLayout><ShareMappedTalent /></HomeLayout> },
        { path: '/market-intel/:id/customerSales', element: <HomeLayout><ShareMappedTalent /></HomeLayout> },
    { path: '/public/job/:job_id/role-playbook', element: <HomeLayout><PublicRolePlaybookPage /></HomeLayout> },

    // Applications route — public if candidateId in URL, otherwise requires auth
    { path: '/job/applications/:stage/:id/:job', element: <ApplicationRoute><HomeLayout><Applications /></HomeLayout></ApplicationRoute> },
    { path: '/job/applications/:stage/:job_id/:job/:id', element: <ApplicationRoute><HomeLayout><Applications /></HomeLayout></ApplicationRoute> },

    // Protected routes (with MUI Container for layout)
    {
      element: <ProtectedRoute><Container maxWidth="xl" sx={{ minHeight: '92vh', p: 0 }}><Outlet /></Container></ProtectedRoute>,
      children: [
        {
          path: '/',
          element: <HomeLayout>{userRole.role === 'client' ? <Dashboard /> : <JobPage />}</HomeLayout>
        },
        { path: '/job-debriefing', element: <HomeLayout><CreateJobDebriefing /></HomeLayout> },
        { path: '/job/:job_id/role-playbook', element: <HomeLayout><RolePlaybookPage /></HomeLayout> },
        { path: '/dashboard', element: <HomeLayout><Dashboard /></HomeLayout> },
        { path: '/job-report', element: <HomeLayout><MainDashboardComponent /></HomeLayout> },
        { path: '/daily-report', element: <HomeLayout><DailyReportComponent /></HomeLayout> },
        { path: '/job-analysis', element: <HomeLayout><JobAnalysis /></HomeLayout> },
        { path: '/job-analytics', element: <HomeLayout><JobAnalyticsNew /></HomeLayout> },
        { path: '/admin-dashboard', element: <HomeLayout><AdminDashboard /></HomeLayout> },
        { path: '/hiring-report', element: <HomeLayout><HiringReport /></HomeLayout> },
        { path: '/job-details/:jobId', element: <HomeLayout><JobDetails /></HomeLayout> },
        { path: '/job-analytics/:clientId', element: <HomeLayout><JobAnalyticsNew /></HomeLayout> },
        { path: '/candidate-report', element: <HomeLayout><CandidateReport /></HomeLayout> },
        { path: '/client/passwordandsecurity', element: <HomeLayout><Change_password /></HomeLayout> },
        { path: '/client/preview/:clientName/:clientId', element: <HomeLayout><PreviewCompany /></HomeLayout> },
        { path: '/client/branding', element: <Branding /> },
        { path: '/client/editprofile', element: <HomeLayout><EditProfile /></HomeLayout> },
        { path: '/client/settings', element: <Settings /> },
        { path: '/InterviewList', element: <HomeLayout><InterviewListTab /></HomeLayout> },
        { path: '/market-intel', element: <HomeLayout><MappingTab /></HomeLayout> },
        { path: '/marketIntel/:marketId', element: <HomeLayout><MappedCandidates /></HomeLayout> },
        { path: '/market-intel/:id', element: <HomeLayout><ShareMappedTalent /></HomeLayout> },
        { path: '/market-intel/:id', element: <HomeLayout><ShareMappedTalent /></HomeLayout> },
        { path: '/talent-pool', element: <HomeLayout><TalentPoolNew /></HomeLayout> },
        { path: '/talent-pool/:clientId', element: <HomeLayout><ClientTalentPoolNew /></HomeLayout> },
        // { path: '/talent_pool/candidate/:cand_id/:stage/sales', element: <NewTalentCardUI /> },
        { path: '/sharedMapping/:id/customerSales', element: <HomeLayout><ShareMappedTalent /></HomeLayout> },
        { path: '/talent-pool/candidate/:cand_id', element: <HomeLayout><TalentPoolNew /></HomeLayout> },
        { path: '/job/:job_id/candidate/:cand_id', element: <FinalTalentCard /> },
        { path: '/talent_pool/candidate/:cand_id/:stage/public', element: <NewTalentCardUI /> },
        { path: '/job/:job_id/candidate/:cand_id/:stage/public', element: <NewTalentCardUI /> },
        // { path: '/job/:job_id/candidate/:cand_id/:stage/sales', element: <FinalTalentCard /> },
        { path: '/team-members', element: <HomeLayout><TeamMembers /></HomeLayout> },
        { path: '/impersonate-users', element: <HomeLayout><ImpersonateUsers /></HomeLayout> },
        { path: '/add-pod', element: <HomeLayout><AddPod /></HomeLayout> },
        { path: '/curation', element: <HomeLayout><Scorecard /></HomeLayout> },
        { path: '/new-talentcard', element: <HomeLayout><NewTalentCard /></HomeLayout> },
        { path: '/changepassword', element: <HomeLayout><Change_Password_Admin /></HomeLayout> },
        { path: '/jobs', element: <HomeLayout><JobPage /></HomeLayout> },
        { path: '/job/:job_id/candidate/:cand_id/:stage', element: <Box><NavBar /><NewTalentCardUI /></Box> },
        { path: '/talent-pool/candidate/:cand_id/bio', element: <ClientTalentPoolNew /> },
        { path: '/talent_pool/candidate/:cand_id/bio/interview', element: <NewTalentCardUI isPublic={true} /> },
        { path: '/job/:job_id/candidate/:cand_id/bio/interview', element: <NewTalentCardUI isPublic={true} /> },
        { path: '/account-view/brand/:id', element: <HomeLayout><AccountView isPublic={true} /></HomeLayout> },
        { path: '/clientTab', element: <HomeLayout><ClientTab /></HomeLayout> },
        { path: '/talent-pool/custom', element: <HomeLayout><TalentPoolNew isPublic={true} /></HomeLayout> },
        { path: '/new-talent-card/talent_pool/candidate/:cand_id/:stage', element: <NewTalentCardUI /> },
        { path: '/expired', element: <HomeLayout><ExpiryPage /></HomeLayout> },
        { path: '/account-view', element: <HomeLayout><AccountView isPublic={true} /></HomeLayout> },
        { path: '/talentpartner-photo-upload', element: <HomeLayout><TalentPartnerPhotoUpload /></HomeLayout> },
      ]
    },
    // Not found
    { path: '*', element: <NotFound /> },
  ]);
  return routes;
}
